import React, { useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2 } from 'react-feather';
import { ConfigToolbar, ToolbarButton, ConfigEmptyState, ConfirmPopover } from '../../components/ConfigPages';
import { deleteRevenueBasisGroup } from '../../utils/revenueBasisApi';

function GroupRow({ group, positionCount, classificationName, onEdit, onDelete }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const members = group.members || [];
  return (
    <tr className="cp-row">
      <td style={{ minWidth: 220 }}>
        <div className="cp-cell" style={{ padding: '8px 12px' }}>
          <div className="cp-name">{group.name}</div>
          {group.code ? <div className="cp-code">{group.code}</div> : null}
          {group.description ? <div className="cp-help" style={{ marginTop: 3, maxWidth: '48ch' }}>{group.description}</div> : null}
        </div>
      </td>
      <td>
        <div className="cp-cell" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 12px' }}>
          {members.length === 0 ? <span className="cp-help">No classifications</span> : null}
          {members.map((m) => (
            <span key={m.id || `${group.id}-${m.typeClassificationId}`} className="cp-tag" title={m.includeDescendants ? 'Includes child classifications' : 'This classification only'}>
              {m.typeClassificationName || classificationName(m.typeClassificationId) || m.typeClassificationId}
              {m.includeDescendants ? <span style={{ marginLeft: 4, color: 'var(--lodge-onyx-400)', fontWeight: 500 }}>+ children</span> : null}
            </span>
          ))}
        </div>
      </td>
      <td style={{ width: 110 }}>
        <div className="cp-cell cp-mono" style={{ color: positionCount ? 'var(--lodge-onyx-900)' : 'var(--lodge-onyx-400)' }}>{positionCount}</div>
      </td>
      <td style={{ width: 90 }}>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', paddingRight: 8 }}>
          <div className="cp-row-actions">
            <ToolbarButton small iconOnly variant="ghost" icon={Edit2} title="Edit revenue basis" aria-label="Edit revenue basis" onClick={() => onEdit(group)} />
            <ToolbarButton small iconOnly variant="ghost" icon={Trash2} title="Delete revenue basis" aria-label="Delete revenue basis" onClick={() => setConfirm(true)} />
          </div>
          <ConfirmPopover
            open={confirm}
            onClose={() => setConfirm(false)}
            busy={busy}
            title={`Delete ${group.name}?`}
            body={positionCount > 0
              ? `${positionCount} position${positionCount !== 1 ? 's' : ''} use it and will fall back to their classification.`
              : 'No positions use it.'}
            onConfirm={async () => { setBusy(true); try { await onDelete(group); } finally { setBusy(false); setConfirm(false); } }}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * Revenue basis tab — the revenue classifications a position's labor is measured
 * against for %-of-revenue reporting.
 */
export default function RevenueBasisTab({
  groups, setGroups, positions, typeClassifications, onCreate, onEdit, reloadPositions,
}) {
  const [q, setQ] = useState('');
  const live = useMemo(() => positions.filter((p) => !p.isDeleted), [positions]);
  const classificationName = (id) => typeClassifications.find((t) => t.id === id)?.name || '';

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    const list = n
      ? groups.filter((g) => g.name?.toLowerCase().includes(n) || g.code?.toLowerCase().includes(n) || g.description?.toLowerCase().includes(n)
        || (g.members || []).some((m) => (m.typeClassificationName || classificationName(m.typeClassificationId)).toLowerCase().includes(n)))
      : groups;
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groups, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDelete = async (group) => {
    try {
      await deleteRevenueBasisGroup(group.id);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      toast.success(`Deleted ${group.name}`);
      // The backend does not clear positions.revenueBasisGroupId — refetch so the
      // Positions tab shows what is actually stored instead of a guess.
      await reloadPositions();
    } catch (err) {
      console.error('Error deleting revenue basis group:', err);
      toast.error(`Could not delete ${group.name} — try again`);
    }
  };

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: q, onChange: setQ, placeholder: 'Search basis, code, classification…' }}
        meta={`${groups.length} ${groups.length === 1 ? 'basis' : 'bases'}`}
        actions={<ToolbarButton variant="primary" icon={Plus} onClick={onCreate}>Add revenue basis</ToolbarButton>}
      />
      {rows.length === 0 ? (
        <ConfigEmptyState
          title={groups.length === 0 ? 'No revenue bases yet' : 'No bases match'}
          body={groups.length === 0
            ? 'A revenue basis groups the revenue classifications a position’s labor cost is measured against, e.g. "Banquet — all revenue" for banquet staff. Without one, a position uses its own classification.'
            : 'Try a different search.'}
          action={groups.length === 0 ? <ToolbarButton variant="primary" icon={Plus} onClick={onCreate}>Add revenue basis</ToolbarButton> : null}
        />
      ) : (
        <div className="it-scroll">
          <table className="it-tbl" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Basis</th>
                <th>Revenue classifications</th>
                <th style={{ width: 110 }}>Positions</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <GroupRow
                  key={g.id}
                  group={g}
                  positionCount={live.filter((p) => p.revenueBasisGroupId === g.id).length}
                  classificationName={classificationName}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
